import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Mail, School, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { YEAR_PRESETS, PRESET_LABELS } from "../../lib/yearPresets";
import { useAuth } from "../../context/AuthContext";

export default function SettingsAdmin() {
  const [profile, setProfile] = useState(null);
  const [schoolName, setSchoolName] = useState("");
  const [yearGroups, setYearGroups] = useState([]);
  const [subGroups, setSubGroups] = useState({});
  const [newSg, setNewSg] = useState({});
  const [newYear, setNewYear] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [smtp, setSmtp] = useState({ host: "", port: 465, user: "", password: "", from_email: "" });

  useEffect(() => {
    api.get("/school/profile").then((r) => {
      setProfile(r.data);
      setSchoolName(r.data.name);
      setYearGroups(r.data.year_groups);
      setSubGroups(r.data.sub_groups || {});
      setSmtp(r.data.smtp || { host: "", port: 465, user: "", password: "", from_email: "" });
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const saveProfile = async () => {
    try {
      await api.put("/school/profile", { name: schoolName, year_groups: yearGroups, sub_groups: subGroups });
      toast.success("School profile saved");
    } catch (e) {
      toast.error(apiError(e));
      api.get("/school/profile").then((r) => {
        setSchoolName(r.data.name);
        setYearGroups(r.data.year_groups);
        setSubGroups(r.data.sub_groups || {});
      }).catch(() => {});
    }
  };

  const saveSmtp = async () => {
    try {
      await api.put("/school/smtp", smtp);
      toast.success("SMTP settings saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/school/account", { data: { password: deletePw } });
      toast.success("School account deleted");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
      setDeleting(false);
    }
  };

  if (!profile) return <div className="font-display font-bold animate-pulse" data-testid="settings-loading">Loading…</div>;

  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <span className="nb-label">Settings</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">School settings</h1>

      <div className="mt-8 nb-card p-6">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><School className="w-5 h-5" /> Profile & year groups</h2>
        <div className="mt-5 space-y-5">
          <div>
            <label className="nb-label" htmlFor="settings-school-name">School name</label>
            <input id="settings-school-name" className="nb-input mt-2" data-testid="settings-school-name"
              value={schoolName} onChange={(e) => setSchoolName(e.target.value)} />
          </div>
          <div>
            <label className="nb-label">Year groups (used for notice targeting & pupil sign-up)</label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="year-preset-row">
              {Object.entries(YEAR_PRESETS).map(([k, v]) => (
                <button key={k} type="button" data-testid={`year-preset-${k}`} onClick={() => setYearGroups([...v])}
                  className="nb-btn bg-white px-3 py-1.5 text-xs">{PRESET_LABELS[k]}</button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-400 font-semibold mt-1">Pick a preset or edit the list below, then Save profile.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {yearGroups.map((y) => (
                <span key={y} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] text-sm font-bold" data-testid={`year-group-chip-${y.replace(/\s+/g, "-")}`}>
                  {y}
                  <button type="button" onClick={() => setYearGroups(yearGroups.filter((x) => x !== y))} data-testid={`year-group-remove-${y.replace(/\s+/g, "-")}`}
                    className="hover:text-red-600 transition-colors duration-150"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input className="nb-input flex-1" placeholder="e.g. Year 12" data-testid="new-year-group-input"
                value={newYear} onChange={(e) => setNewYear(e.target.value)} />
              <button type="button" data-testid="add-year-group-btn"
                onClick={() => { if (newYear.trim() && !yearGroups.includes(newYear.trim())) setYearGroups([...yearGroups, newYear.trim()]); setNewYear(""); }}
                className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm"><Plus className="w-4 h-4" /> Add</button>
            </div>
          </div>
          <div>
            <label className="nb-label">Sub-groups per year (optional — e.g. 1A, 1B)</label>
            <div className="mt-2 space-y-2">
              {yearGroups.map((y) => (
                <div key={y} className="flex flex-wrap items-center gap-2" data-testid={`subgroup-row-${y.replace(/\s+/g, "-")}`}>
                  <span className="text-sm font-bold w-20 shrink-0">{y}</span>
                  {(subGroups[y] || []).map((g) => (
                    <span key={g} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border-2 border-zinc-900 bg-[#7DD3FC] text-xs font-bold" data-testid={`subgroup-chip-${y.replace(/\s+/g, "-")}-${g}`}>
                      {g}
                      <button type="button" data-testid={`subgroup-remove-${y.replace(/\s+/g, "-")}-${g}`}
                        onClick={() => setSubGroups({ ...subGroups, [y]: subGroups[y].filter((x) => x !== g) })}
                        className="hover:text-red-600 transition-colors duration-150"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  <input className="nb-input h-8 py-1 w-24 text-xs" placeholder="+ add (Enter)" data-testid={`subgroup-input-${y.replace(/\s+/g, "-")}`}
                    value={newSg[y] || ""}
                    onChange={(e) => setNewSg({ ...newSg, [y]: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      const v = (newSg[y] || "").trim();
                      if (v && !(subGroups[y] || []).includes(v)) setSubGroups({ ...subGroups, [y]: [...(subGroups[y] || []), v] });
                      setNewSg({ ...newSg, [y]: "" });
                    }} />
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-400 font-semibold mt-2">Pupils pick their sub-group when joining. Teachers can then target homework at a sub-group.</p>
          </div>
          <button onClick={saveProfile} data-testid="save-profile-btn" className="nb-btn bg-[#1E3A8A] text-white px-5 py-2.5">
            <Save className="w-4 h-4" /> Save profile
          </button>
        </div>
      </div>

      <div className="mt-6 nb-card p-6">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><Mail className="w-5 h-5" /> Email (SMTP) for notices</h2>
        <p className="text-sm text-zinc-500 mt-2">
          Notices are emailed to parent addresses on file. Add your school's own SMTP server below — if left blank, the platform fallback server is used (port 465 SSL by default).
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="nb-label" htmlFor="smtp-host">SMTP host</label>
            <input id="smtp-host" className="nb-input mt-2" data-testid="smtp-host-input" placeholder="smtp.school.sch.uk"
              value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="smtp-port">Port</label>
            <input id="smtp-port" type="number" className="nb-input mt-2" data-testid="smtp-port-input" placeholder="465"
              value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) || 465 })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="smtp-user">Username</label>
            <input id="smtp-user" className="nb-input mt-2" data-testid="smtp-user-input"
              value={smtp.user} onChange={(e) => setSmtp({ ...smtp, user: e.target.value })} />
          </div>
          <div>
            <label className="nb-label" htmlFor="smtp-password">Password (leave blank to keep saved)</label>
            <input id="smtp-password" type="password" className="nb-input mt-2" data-testid="smtp-password-input"
              value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="nb-label" htmlFor="smtp-from">From email</label>
            <input id="smtp-from" type="email" className="nb-input mt-2" data-testid="smtp-from-input" placeholder="office@school.sch.uk"
              value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} />
          </div>
        </div>
        <button onClick={saveSmtp} data-testid="save-smtp-btn" className="nb-btn bg-[#FDE68A] px-5 py-2.5 mt-5">
          <Save className="w-4 h-4" /> Save SMTP
        </button>
      </div>

      <div className="mt-6 nb-card p-6 border-red-300 bg-red-50/50" data-testid="danger-zone">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 text-red-700"><Trash2 className="w-5 h-5" /> Danger zone</h2>
        <p className="text-sm text-zinc-600 mt-2">
          Deleting the school account permanently removes all pupils, teachers, timetables, notices, homework and exam data. This can't be undone.
        </p>
        <button onClick={() => setShowDelete(true)} data-testid="delete-school-btn" className="nb-btn bg-red-100 text-red-700 px-5 py-2.5 mt-4">
          <Trash2 className="w-4 h-4" /> Delete school account
        </button>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="delete-school-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <h3 className="font-display font-bold text-xl text-red-700">Delete {schoolName}?</h3>
            <p className="text-sm text-zinc-600 mt-2">Enter your admin password to confirm. Any active Stripe subscription is cancelled and all school data is removed permanently.</p>
            <input type="password" className="nb-input mt-4" data-testid="delete-school-password" placeholder="Your password"
              value={deletePw} onChange={(e) => setDeletePw(e.target.value)} />
            <div className="flex gap-3 mt-4">
              <button onClick={deleteAccount} disabled={!deletePw || deleting} data-testid="delete-school-confirm" className="nb-btn flex-1 bg-red-600 text-white py-2.5">
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
              <button onClick={() => { setShowDelete(false); setDeletePw(""); }} data-testid="delete-school-cancel" className="nb-btn bg-white px-4 py-2.5">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
